import { useAnnouncementsQuery } from '@/hooks/queries/use-announcements-query';
import { useProductAnnouncementStore } from '@/stores/product-announcement-store';
import type { ProductAnnouncement } from '@fluenceur/types';
import { useEffect, useMemo } from 'react';

interface UseProductAnnouncementsResult {
  /** Undismissed product updates for the brand, in display order. */
  products: ProductAnnouncement[];
  /** How many of those the operator has not had on screen yet. */
  unseenCount: number;
}

/**
 * Single source of truth for the product-update subset of the mixed
 * announcement feed. Both the top-bar indicator and the floating panel call
 * it: TanStack Query dedupes the request, and the read state comes from the
 * shared store so the two stay consistent without prop drilling through
 * `Layout`.
 */
export function useProductAnnouncements(brandId: string | null): UseProductAnnouncementsResult {
  const announcements = useAnnouncementsQuery(brandId);
  const hydrate = useProductAnnouncementStore((state) => state.hydrate);
  const seenIds = useProductAnnouncementStore((state) => state.seenIds);
  const hydratedBrandId = useProductAnnouncementStore((state) => state.brandId);

  useEffect(() => {
    hydrate(brandId);
  }, [brandId, hydrate]);

  const products = useMemo(
    () =>
      (announcements.data ?? [])
        .filter((item): item is ProductAnnouncement => item.kind === 'product')
        .sort(
          (left, right) => left.displayOrder - right.displayOrder || left.id.localeCompare(right.id)
        ),
    [announcements.data]
  );

  const unseenCount = useMemo(() => {
    // Before hydration lands for this brand the stored ids belong to the
    // previous one; counting them would flash a wrong badge.
    if (hydratedBrandId !== brandId) return products.length;
    return products.filter((product) => !seenIds.includes(product.id)).length;
  }, [brandId, hydratedBrandId, products, seenIds]);

  return { products, unseenCount };
}
